import type { IRequestOpts, IResultstash, JsonQuery } from "@mne-core/api";
import { QueryController, createCsrfToken, createPostRequestOpts, createUrl, fetchJsonQuery } from "@mne-core/api";

import type { IAuthenticationOauthData } from "../@types";

/**
 * Makes an authorization request to an OAuth client following OAuth authorization flow.
 * This operation authorizes the user to access a specified OAuth client and returns an authorization token,
 * which can be used by the client to exchange for an access and/or ID token using the token endpoint.
 *
 * @interface
 * @param {Object} params - The authorization request parameters.
 * @param {'code' | 'id_token' | 'token'} params.responseType - The type of response requested.
 * @param {string} params.clientId - The client ID of the requesting client.
 * @param {string} params.redirectUri - The redirect URI requested by the client.
 * @param {string} [params.state] - An optional state to be returned with the redirect after a successful authorization.
 * @param {string} [params.nonce] - A nonce value that should be included unmodified in the subsequent token response.
 * @param {string[]} [params.scope] - The scopes of access requested. If 'openid' is included, this is an OpenID request.
 * @returns {Promise<AuthorizationToken>} - A promise that resolves to the authorization token.
 */

export interface IAuthenticateOauthCriteria {
  responseType: string;
  clientID: string;
  redirectURI: string;
  state?: string;
  nonce?: string;
  scope?: string[];
}

export const createRequest = (criteria: IAuthenticateOauthCriteria, opts: IRequestOpts): Request => {
  const { apiUrl, sessionKey } = opts;
  const uri = createUrl(apiUrl, ["authentication", "v1", "oauth", "authorize"]).toString();
  return new Request(
    uri,
    createPostRequestOpts(JSON.stringify(criteria), { ...opts, csrfToken: createCsrfToken(sessionKey, uri) }),
  );
};

async function authenticateOauth(
  criteria: IAuthenticateOauthCriteria,
  opts: IRequestOpts,
): Promise<JsonQuery<IResultstash<IAuthenticationOauthData>>> {
  return await fetchJsonQuery<IResultstash<IAuthenticationOauthData>>(createRequest(criteria, opts), {
    ctrl: opts.ctrl || new QueryController(),
  });
}
export default authenticateOauth;
